'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchAllDiscounts } from '../../services/apiService';
import { normalizeDeal } from './HeroShowcase';
import ProductRailPanel from './ProductRailPanel';

/**
 * Standalone offers as a swipeable rail on a brand-red panel, each card
 * carrying its tilted percentage sticker. Campaign discounts render in their
 * own CampaignSection panels, so this asks for standalone ones only ('none').
 *
 * Only the mobile tree of Home renders this now — on desktop the offers live
 * in the hero's showcase (HeroShowcase).
 */
const Discount = React.memo(() => {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchAllDiscounts(1, 20, 'none');
        if (!cancelled) {
          setProducts(response?.success ? (response.data?.products || []).slice(0, 20) : []);
        }
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const items = useMemo(() => products.map(normalizeDeal).filter(Boolean), [products]);

  return (
    <ProductRailPanel
      tone="red"
      lead={t('home.discount.title')}
      highlight={t('home.discount.titleHighlight')}
      subtitle={t('home.discount.subtitle')}
      items={items}
      loading={loading}
      variant="deal"
      viewAllLabel={t('home.discount.viewAll')}
      viewAllPath="/products?view=discount"
    />
  );
});

Discount.displayName = 'Discount';

export default Discount;
